/**
 * RichTextEditor (DESIGN.md §5 "Rich content", T11): the one editor for mail and signatures,
 * built on TipTap (decision: docs/research/ui/rich-text-editor.md).
 *
 * - Value: `{ format: 'html' | 'text', content }`. HTML is the editor's e-mail-safe subset with
 *   images as data: URLs (mail/outgoing.ts turns them into cid: attachments on send).
 * - Toolbar with shortcuts (Ctrl+B/I/U/K, Ctrl+Z/Y, Ctrl+M indent, lists, quote, alignment).
 * - Inline images by paste, drag and drop or the toolbar button; paste cleanup for Word/Outlook/web.
 * - Plain-text mode: the user can switch (with confirmation, formatting is lost) and back.
 * - Handle (`handleRef`): focus and the signature hook (`setSignature`) for the signature service.
 *
 * Labels come from UiStrings (`editor`), so every app gets the same localized editor.
 */
import { getSchema } from '@tiptap/core';
import { EditorContent, useEditor, type Editor } from '@tiptap/react';
import { lazy, Suspense, useImperativeHandle, useLayoutEffect, useMemo, useRef, useState, type Ref } from 'react';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/alert-dialog';
import { Button } from '../components/button';
import { SegmentedControl } from '../components/form-controls';
import { Textarea } from '../components/input';
import { Skeleton } from '../components/misc';
import { toast } from '../components/sonner';
import { cn } from '../lib/utils';
import { useUiStrings } from '../strings';
import { editorExtensions } from './extensions';
import { imageFiles, isSupportedImage, MAX_INLINE_IMAGE_BYTES, readImages, INLINE_IMAGE_TYPES } from './images';
import type { RichTextEditorLabels } from './labels';
import { LinkDialog } from './link-dialog';
import { cleanPastedHtml } from './paste';
import { documentToPlainText, plainTextToEditorHtml } from './plain-text';
import { isLossy, prettyHtml } from './source';
import type { SourceEditorHandle } from './source-editor';
import { EditorToolbar } from './toolbar';

// CodeMirror is a separate chunk, loaded when a source mode is chosen.
const SourceEditor = lazy(() => import('./source-editor').then((module) => ({ default: module.SourceEditor })));

export type RichTextFormat = 'html' | 'text';

/** Input modes: the visual editor, the HTML code view of the same document, plain text. */
export type EditorMode = 'formatted' | 'source' | 'plain';
const MODE_ORDER: readonly EditorMode[] = ['formatted', 'source', 'plain'];
const DEFAULT_MODES: readonly EditorMode[] = ['formatted', 'plain'];

export interface RichTextValue {
  format: RichTextFormat;
  content: string;
  /** `true` when `content` is the raw HTML typed in source mode (kept as is, not normalized by the visual editor). */
  source?: boolean;
}

export interface RichTextEditorHandle {
  focus: () => void;
  /** Inserts or replaces the signature block (marker `data-gx-sig`); no-op in plain-text mode. */
  setSignature: (signature: { id: string; html: string }) => void;
  /** Removes the signature block, if any; no-op in plain-text mode. */
  removeSignature: () => void;
  /** Inserts text at the cursor (e.g. a `{placeholder}` of a signature template). */
  insertText: (text: string) => void;
}

export interface RichTextEditorProps {
  defaultValue?: RichTextValue;
  onChange: (value: RichTextValue) => void;
  /** Accessible name of the text area. */
  label: string;
  placeholder?: string;
  autoFocus?: boolean;
  className?: string;
  /** Opt in to a compact phone toolbar; desktop formatting controls stay unchanged. */
  compactToolbar?: boolean;
  handleRef?: Ref<RichTextEditorHandle>;
  /**
   * Files pasted or dropped into the text that are not inline pictures (documents, archives, and
   * pictures the editor cannot embed): the composer turns them into attachments. Without it they
   * are ignored (pictures get the usual error).
   */
  onFiles?: (files: File[]) => void;
  /**
   * Input modes offered to the user (default: formatted + plain text, the composer). Including `source` adds a mode
   * switch with an HTML code view: signatures and, later, scanner banners and notifications. In source mode the
   * raw HTML is the value (`source: true`); content the visual editor cannot represent starts in source mode.
   */
  modes?: readonly EditorMode[];
}

/** Splits pasted/dropped files into pictures the text can show and files for the attachment list. */
function splitFiles(list: FileList | null | undefined, attach: boolean): { inline: File[]; other: File[] } {
  const files = Array.from(list ?? []);
  if (!attach) return { inline: imageFiles(list), other: [] };
  const fits = (file: File) => isSupportedImage(file) && file.size <= MAX_INLINE_IMAGE_BYTES;
  return { inline: files.filter(fits), other: files.filter((file) => !fits(file)) };
}

const formatMegabytes = (bytes: number) => `${Math.round(bytes / (1024 * 1024))} MB`;

async function insertImages(editor: Editor, files: File[], labels: RichTextEditorLabels, position?: number): Promise<void> {
  const { images, rejected } = await readImages(files);
  for (const { name, reason } of rejected) {
    toast.error(
      reason === 'tooLarge'
        ? labels.imageTooLarge.replace('{name}', name).replace('{max}', formatMegabytes(MAX_INLINE_IMAGE_BYTES))
        : labels.imageUnsupported.replace('{name}', name),
    );
  }
  if (images.length === 0) return;
  const content = images.map((image) => ({ type: 'image', attrs: { src: image.src, alt: image.name } }));
  const chain = editor.chain().focus();
  (position === undefined ? chain.insertContent(content) : chain.insertContentAt(position, content)).run();
}

export function RichTextEditor({ defaultValue, onChange, label, placeholder, autoFocus, className, handleRef, onFiles, compactToolbar = false, modes = DEFAULT_MODES }: RichTextEditorProps) {
  const labels = useUiStrings().editor;
  const hasSource = modes.includes('source');
  // Content the visual editor cannot represent starts in source mode (when offered), so nothing is lost by editing.
  const [initialMode] = useState<EditorMode>(() => {
    if (defaultValue?.format === 'text') return 'plain';
    if (hasSource && defaultValue?.content && isLossy(defaultValue.content, getSchema(editorExtensions({ onLinkShortcut: () => undefined })))) return 'source';
    return 'formatted';
  });
  const [mode, setMode] = useState<EditorMode>(initialMode);
  const [source, setSource] = useState(initialMode === 'source' ? (defaultValue?.content ?? '') : '');
  const [text, setText] = useState(defaultValue?.format === 'text' ? defaultValue.content : '');
  const [linkOpen, setLinkOpen] = useState(false);
  const [confirm, setConfirm] = useState<'plain' | 'markup' | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const textArea = useRef<HTMLTextAreaElement>(null);
  const sourceHandle = useRef<SourceEditorHandle>(null);
  const labelsRef = useRef(labels);
  const onChangeRef = useRef(onChange);
  const onFilesRef = useRef(onFiles);

  const editor = useEditor({
    extensions: editorExtensions({ placeholder, onLinkShortcut: () => setLinkOpen(true) }),
    content: defaultValue?.format === 'html' && initialMode === 'formatted' ? defaultValue.content : '',
    autofocus: autoFocus && initialMode === 'formatted' ? 'start' : false,
    editorProps: {
      attributes: { role: 'textbox', 'aria-multiline': 'true', 'aria-label': label, class: 'rich-text-content' },
      transformPastedHTML: cleanPastedHtml,
      handlePaste: (_view, event) => {
        // Pictures from the clipboard (screenshots). Office puts a picture next to the HTML when
        // copying text, so a picture is only inserted when there is no HTML.
        if (event.clipboardData?.types.includes('text/html')) return false;
        const { inline, other } = splitFiles(event.clipboardData?.files, onFilesRef.current !== undefined);
        if (inline.length === 0 && other.length === 0) return false;
        if (other.length > 0) onFilesRef.current?.(other);
        if (inline.length > 0 && editorRef.current) void insertImages(editorRef.current, inline, labelsRef.current);
        return true;
      },
      handleDrop: (view, event) => {
        const { inline, other } = splitFiles(event.dataTransfer?.files, onFilesRef.current !== undefined);
        if ((inline.length === 0 && other.length === 0) || !editorRef.current) return false;
        event.preventDefault();
        // Handled here: the composer's drop zone must not attach the same files again.
        event.stopPropagation();
        if (other.length > 0) onFilesRef.current?.(other);
        const position = view.posAtCoords({ left: event.clientX, top: event.clientY })?.pos;
        if (inline.length > 0) void insertImages(editorRef.current, inline, labelsRef.current, position);
        return true;
      },
    },
    onUpdate: ({ editor: e }) => onChangeRef.current({ format: 'html', content: e.getHTML() }),
  });
  const editorRef = useRef<Editor | null>(null);
  // The handlers above run later (paste, drop, updates): keep them pointed at the current values.
  useLayoutEffect(() => {
    editorRef.current = editor;
    labelsRef.current = labels;
    onChangeRef.current = onChange;
    onFilesRef.current = onFiles;
  });

  useImperativeHandle(
    handleRef,
    () => ({
      focus: () => {
        if (mode === 'plain') textArea.current?.focus();
        else if (mode === 'source') sourceHandle.current?.focus();
        else editor.commands.focus();
      },
      setSignature: (signature) => {
        if (mode === 'formatted') editor.commands.setSignature(signature);
      },
      removeSignature: () => {
        if (mode === 'formatted') editor.commands.removeSignature();
      },
      insertText: (value) => {
        if (mode === 'formatted') {
          editor.chain().focus().insertContent(value).run();
          return;
        }
        if (mode === 'source') {
          sourceHandle.current?.insert(value);
          return;
        }
        const area = textArea.current;
        if (!area) return;
        const next = area.value.slice(0, area.selectionStart) + value + area.value.slice(area.selectionEnd);
        setText(next);
        onChangeRef.current({ format: 'text', content: next });
      },
    }),
    [editor, mode],
  );

  // The visual editor can drop markup it has no node for: warn while the user edits the source.
  const lossy = useMemo(() => mode === 'source' && isLossy(source, editor.schema), [mode, source, editor]);

  const toPlainText = () => {
    if (mode === 'source') editor.commands.setContent(source, { emitUpdate: false });
    const plain = documentToPlainText(editor.state.doc);
    setText(plain);
    setMode('plain');
    onChange({ format: 'text', content: plain });
    requestAnimationFrame(() => textArea.current?.focus());
  };

  const toRichText = () => {
    editor.commands.setContent(mode === 'source' ? source : plainTextToEditorHtml(text), { emitUpdate: false });
    setMode('formatted');
    onChange({ format: 'html', content: editor.getHTML() });
    requestAnimationFrame(() => editor.commands.focus('end'));
  };

  const toSource = () => {
    // The raw source is the value from now on; the pretty-printed text only adds line breaks between blocks.
    const html = prettyHtml(editor.getHTML());
    setSource(html);
    setMode('source');
    onChange({ format: 'html', content: html, source: true });
  };

  /** Mode switch: leaving the visual editor for plain text and source → visual may lose content, so they ask. */
  const requestMode = (target: EditorMode) => {
    if (target === mode) return;
    if (target === 'plain') setConfirm('plain');
    else if (target === 'formatted') {
      if (mode === 'plain' || !lossy) toRichText();
      else setConfirm('markup');
    } else {
      if (mode === 'plain') toRichText();
      toSource();
    }
  };

  const modeOptions = MODE_ORDER.filter((option) => modes.includes(option)).map((option) => ({
    value: option,
    label: { formatted: labels.modeFormatted, source: labels.modeSource, plain: labels.modePlain }[option],
  }));
  const modeSwitch = hasSource && modeOptions.length > 1 && (
    <div className="flex items-center border-b bg-muted/40 px-1 py-1">
      <SegmentedControl compact label={labels.modeLabel} value={mode} options={modeOptions} onChange={requestMode} />
    </div>
  );

  return (
    <div className={cn('flex min-h-0 min-w-0 flex-col overflow-hidden rounded-md border border-input bg-background', className)}>
      {modeSwitch}
      {mode === 'source' ? (
        <>
          <Suspense fallback={<Skeleton aria-busy="true" className="min-h-0 flex-1 rounded-none" />}>
            <SourceEditor
              handleRef={sourceHandle}
              value={source}
              label={`${label}: ${labels.sourceLabel}`}
              autoFocus
              onChange={(next) => {
                setSource(next);
                onChange({ format: 'html', content: next, source: true });
              }}
            />
          </Suspense>
          {lossy && (
            <p role="status" className="border-t bg-warning/15 px-3 py-1.5 text-xs">
              {labels.sourceUnsupported}
            </p>
          )}
        </>
      ) : mode === 'formatted' ? (
        <>
          <EditorToolbar
            editor={editor}
            labels={labels}
            compactToolbar={compactToolbar}
            onLink={() => setLinkOpen(true)}
            onImage={() => fileInput.current?.click()}
            onPlainText={modeSwitch ? undefined : () => setConfirm('plain')}
          />
          <EditorContent editor={editor} className="min-h-0 min-w-0 flex-1 overflow-y-auto" />
          <input
            ref={fileInput}
            type="file"
            accept={INLINE_IMAGE_TYPES.join(',')}
            multiple
            hidden
            onChange={(event) => {
              const files = Array.from(event.target.files ?? []);
              event.target.value = '';
              void insertImages(editor, files, labels);
            }}
          />
          <LinkDialog editor={editor} labels={labels} open={linkOpen} onOpenChange={setLinkOpen} />
        </>
      ) : (
        <>
          {!modeSwitch && (
            <div className="flex items-center justify-end border-b bg-muted/40 px-1 py-0.5">
              <Button variant="ghost" size="sm" onClick={toRichText}>
                {labels.richText}
              </Button>
            </div>
          )}
          <Textarea
            ref={textArea}
            aria-label={label}
            placeholder={placeholder}
            autoFocus={autoFocus}
            value={text}
            onChange={(event) => {
              setText(event.target.value);
              onChange({ format: 'text', content: event.target.value });
            }}
            className="min-h-0 flex-1 resize-none rounded-none border-0 shadow-none focus-visible:ring-0"
          />
        </>
      )}
      <AlertDialog open={confirm !== null} onOpenChange={(open) => !open && setConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{confirm === 'markup' ? labels.sourceConfirmTitle : labels.plainTextConfirmTitle}</AlertDialogTitle>
            <AlertDialogDescription>{confirm === 'markup' ? labels.sourceConfirmDescription : labels.plainTextConfirmDescription}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel autoFocus>{labels.cancel}</AlertDialogCancel>
            <AlertDialogAction onClick={confirm === 'markup' ? toRichText : toPlainText}>
              {confirm === 'markup' ? labels.sourceConfirm : labels.plainTextConfirm}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
